import { getYoutubeVideoId } from '../components/videovizitky/video-card-data';
import type {
  ContentFieldsOwner,
  ContentGroup,
} from '@eliadmin/core/src/api/content-fields/content-fields-types';
import { getContentFields } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import {
  getContentTextFields,
  type ContentTextMapping,
} from '@eliadmin/core/src/api/content-fields/content-text-mapping';

import {
  COMPANY_VIDEO_IDS,
  CURATED_VIDEO_NAMES,
  DESTINATION_VIDEO_IDS,
  OFFICIAL_CHANNEL_VIDEO_IDS,
  TOUR_VIDEO_DESTINATIONS,
  TOUR_VIDEO_DESTINATIONS_BY_SLUG,
} from '../data/tour-video-library';

export type TourVideo = { id: string; name: string; kind?: 'destination' | 'company' };
const VIDEO_FIELD_IDS = new Set([4, 5, 6, 7, 8]);

/** Root identity selects the business slot; text is exclusively a child component. */
export function isTourVideoField(field: unknown): boolean {
  if (!field || typeof field !== 'object') return false;
  const candidate = field as ContentGroup;
  if (
    candidate.type !== 'group' ||
    typeof candidate.name !== 'string' ||
    !Array.isArray(candidate.items)
  )
    return false;
  if (!VIDEO_FIELD_IDS.has(candidate.id) && !/^video\s+\d+$/i.test(candidate.name.trim()))
    return false;
  const fields = candidate.items[0]?.fields;
  return (
    Array.isArray(fields) &&
    fields.some(
      component =>
        component?.type === 'textbox' &&
        component.name.trim().toLowerCase() === candidate.name.trim().toLowerCase(),
    )
  );
}

/** Public response order, first ordered item, verified component ID; no hard limit of five slots. */
export function getTourVideoMappings(owner: ContentFieldsOwner): ContentTextMapping[] {
  return getContentFields(owner)
    .filter(isTourVideoField)
    .flatMap(group => {
      const mapping: ContentTextMapping = {
        groupId: group.id,
        component: 'same-name-text',
        itemIndex: 0,
      };
      const value = getContentTextFields(owner, [mapping])[0]?.value;
      return getYoutubeVideoId(value) ? [mapping] : [];
    });
}

export function getTourVideos(owner: ContentFieldsOwner): TourVideo[] {
  const seen = new Set<string>();
  return getContentTextFields(owner, getTourVideoMappings(owner)).flatMap(field => {
    const id = getYoutubeVideoId(field.value);
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [{ id, name: field.name.trim() || `Video ${seen.size}` }];
  });
}

const officialVideoIds = new Set(OFFICIAL_CHANNEL_VIDEO_IDS);
const companyVideoIds = new Set(COMPANY_VIDEO_IDS);

/** Own-channel videos only: authored choices, destination selection, then labelled company clips. */
export function getTourVideoSelection(
  tour: ContentFieldsOwner & {
    id?: number | string;
    slug?: string;
  },
): TourVideo[] {
  const profile = Object.prototype.hasOwnProperty.call(TOUR_VIDEO_DESTINATIONS, String(tour.id))
    ? TOUR_VIDEO_DESTINATIONS[String(tour.id)]
    : tour.slug
      ? TOUR_VIDEO_DESTINATIONS_BY_SLUG[tour.slug]
      : null;
  const selection: TourVideo[] = [];
  const seen = new Set<string>();
  const add = (id: string, name: string, kind: 'destination' | 'company') => {
    if (selection.length >= 8 || seen.has(id) || !officialVideoIds.has(id)) return;
    seen.add(id);
    selection.push({ id, name: CURATED_VIDEO_NAMES[id] || name, kind });
  };

  for (const video of getTourVideos(tour)) {
    add(video.id, video.name, companyVideoIds.has(video.id) ? 'company' : 'destination');
  }
  for (const id of profile ? (DESTINATION_VIDEO_IDS[profile] ?? []) : []) {
    add(id, CURATED_VIDEO_NAMES[id], 'destination');
  }
  for (const id of COMPANY_VIDEO_IDS) add(id, CURATED_VIDEO_NAMES[id], 'company');
  return selection;
}
